import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { defineMessages } from 'react-intl';
import { SubmissionError } from 'redux-form';
import { call, put, all } from 'redux-saga/effects';
import * as logger from 'universal-logger';
import { userProfile } from 'redux/saga/entities/bound';
import { memberStatus } from 'enums/memberStatus';
import { dimensions } from 'components/GoogleAnalytics/GoogleAnalytics';
import { ERROR_INVALID_CREDENTIALS, ERROR_PASSWORD_RESET_REQUIRED } from 'constants/api-error-codes';
import { setUserId, ga, ec } from 'helpers/multitrack';
import { segment, extractSegmentUserProperties } from 'helpers/segment';
import { setToken } from 'redux/modules/user';
import config from 'universal-config';
import fetch from 'universal-fetch';

const FB_NOT_AVAILABLE = 'FB_NOT_AVAILABLE';
const GET_FACEBOOK_TOKEN_FAILED = 'GET_FACEBOOK_TOKEN_FAILED';
const GET_FACEBOOK_PROFILE_FAILED = 'GET_FACEBOOK_PROFILE_FAILED';

export const messages = defineMessages({
  authError: {
    id: 'login.authentication.failed',
    defaultMessage:
      'The password or email address you entered is incorrect. <a href="{resetLink}">Reset your password</a> or <a href="{contactLink}">contact us</a> if youʼre having trouble logging in.',
    values: {
      resetLink: '/reset-password',
      contactLink: '/contact',
    },
  },
  authSecurityError: {
    id: 'location.authentication.security-reset',
    defaultMessage:
      'You are required to change your password for security reasons. Please <a href="{resetLink}">reset your password</a> now.',
    values: {
      resetLink: '/reset-password',
    },
  },
});

export function verifyAndSetToken(client, token) {
  return (client.fetch || fetch)('/_verifyAuthToken', {
    credentials: 'same-origin',
    method: 'post',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ token }),
  })
    .then(res => res.json())
    .then(data => {
      if (!data.success) {
        const err = new Error(data.message);
        err.code = ERROR_INVALID_CREDENTIALS;
        throw err;
      }
      return data;
    });
}

export function* requestLoginWithAuthToken(client, token) {
  const result = { token };
  yield call(verifyAndSetToken, client, token);
  return result;
}

export function* requestLoginWithCredentials(client, email, password) {
  const result = yield call(client.users.loginWithCredentials, {
    username: email,
    password,
    client_id: config().clientId,
  });
  yield call(verifyAndSetToken, client, result.token);
  return result;
}

export function* requestLoginWithFacebookToken(client, token) {
  const result = yield call(client.users.loginWithFacebook, { token, client_id: config().clientId });
  yield call(verifyAndSetToken, client, result.token);
  return result;
}

export function* doHandlePostAuth(client, token, forceReadFromMaster) {
  yield call([client, client.useToken], token);
  yield put(setToken(token));

  const user = yield call(fetchEntityIfNeeded, userProfile, [
    client,
    forceReadFromMaster ? { forceReadFromMaster: true } : {},
  ]);

  let userType = 'registered';
  if (memberStatus('CANCELLED', user.membership.status)) {
    userType = 'churned';
  } else if (memberStatus('ACTIVE', user.membership.status)) {
    userType = 'subscribed';
  }

  yield all([
    call(segment.identify, user.id, {
      user_type: userType,
      ...extractSegmentUserProperties(user),
    }),
    call(segment.alias, user.id),
    call(setUserId, user.id),
    call(segment.track, 'Log In', {
      user_type: userType,
    }),
    call(ga, 'set', dimensions.USER_ID, String(user.id)),
    call(ec, 'Log In', { user_type: userType }),
  ]);

  if (window.Rollbar) {
    window.Rollbar.configure({
      payload: {
        person: {
          id: user.id,
        },
      },
    });
  }
}

export const getFacebookToken = FacebookSDK =>
  new Promise((resolve, reject) =>
    FacebookSDK.login(
      response =>
        response && response.authResponse && response.authResponse.accessToken
          ? resolve(response.authResponse.accessToken)
          : reject(new Error(GET_FACEBOOK_TOKEN_FAILED)),
      { scope: 'email,public_profile,user_friends' }
    )
  );

export const getFacebookProfile = FacebookSDK =>
  new Promise((resolve, reject) =>
    FacebookSDK.api(
      '/me?fields=email,first_name,gender,last_name,link,locale,name,timezone,updated_time,verified',
      response => (!response || response.error ? reject(new Error(GET_FACEBOOK_PROFILE_FAILED)) : resolve(response))
    )
  );

export function* authorizeFacebook(client) {
  const FB = (client && client.FB) || window.FB;

  try {
    if (!FB) {
      throw new Error(FB_NOT_AVAILABLE);
    }

    const facebookToken = yield call(getFacebookToken, FB);
    yield call(getFacebookProfile, FB);
    return facebookToken;
  } catch (err) {
    let message;
    if (err.message === FB_NOT_AVAILABLE) {
      message = 'Facebook login not available at this time.';
    } else if (err.message === GET_FACEBOOK_TOKEN_FAILED) {
      message = 'Facebook login failed.';
    } else {
      yield call(logger.error, 'Facebook login error message', err.message);
      message = 'Sorry, something went wrong communicating with Facebook.';
    }

    throw new Error(message);
  }
}

export function* doLoginWithAuthToken(client, form, authToken) {
  try {
    const result = yield call(requestLoginWithAuthToken, client, authToken);
    return result.token;
  } catch (err) {
    if (form) {
      if (err.code === ERROR_PASSWORD_RESET_REQUIRED) {
        yield put(
          form.failure(
            new SubmissionError({
              _error: messages.authSecurityError,
            })
          )
        );
        return;
      }
    }

    throw err;
  }
}

export function* doLoginWithFacebook(client, form, facebookToken) {
  try {
    if (!facebookToken) {
      facebookToken = yield call(authorizeFacebook);
    }
    const result = yield call(requestLoginWithFacebookToken, client, facebookToken);
    yield call(doHandlePostAuth, client, result.token);
    return result.token;
  } catch (err) {
    let { message } = err;
    if (err.code === ERROR_INVALID_CREDENTIALS) {
      message = messages.authError;
    }
    if (form) {
      yield put(form.failure(new SubmissionError({ _error: message })));
    } else {
      throw new Error(message);
    }
  }
}

export function* doLoginWithCredentials(client, form, email, password) {
  try {
    const result = yield call(requestLoginWithCredentials, client, email, password);
    yield call(doHandlePostAuth, client, result.token);
    return result.token;
  } catch (err) {
    if (err.code === ERROR_INVALID_CREDENTIALS) {
      yield put(
        form.failure(
          new SubmissionError({
            _error: messages.authError,
          })
        )
      );
      return;
    }
    if (err.code === ERROR_PASSWORD_RESET_REQUIRED) {
      yield put(
        form.failure(
          new SubmissionError({
            _error: messages.authSecurityError,
          })
        )
      );
      return;
    }
    throw err;
  }
}

export function* createUserAndLogIn(client, form, payload) {
  const result = yield call(client.users.create, payload);

  /* eslint-disable no-nested-ternary,indent */
  const authToken = result.auth_token
    ? yield call(doLoginWithAuthToken, client, form, result.auth_token)
    : payload.facebook
    ? yield call(doLoginWithFacebook, client, form, payload.facebook.token)
    : payload.user
    ? yield call(doLoginWithCredentials, client, form, payload.user.email, payload.user.password)
    : null;
  /* eslint-enable no-nested-ternary,indent */

  // NOTE: make sure we read the most accurate data after account creation
  if (authToken) {
    yield call(doHandlePostAuth, client, authToken, true);
  }

  return result.id;
}
